import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { DeviceService } from '../../device.service';
import * as moment from 'moment';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { UsersService } from 'src/app/users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';

@Component({
	selector: 'app-glitches',
	templateUrl: './glitches.component.html',
	styleUrls: ['./glitches.component.css']
})
export class GlitchesComponent implements OnInit, OnChanges {

	@Input() device: any = {};
	@Input() dateRange: any = {};
	@Input() invalidDateRange: boolean = false;
	@Input() currentSite: any;
	@Input() enterprisePermissions: any;

	emptyResponse = false;

	allColumnConfig: ColumnsConfig[] = [];
	columnConfig: ColumnsConfig[] = [];
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		fileName: 'glitches',
		hasPagination: true,
		pageSize: 100,
		csvFileHeaderInfo: []
	};

	readonly voltageFields = [5, 6, 7, 8, 9, 10];
	readonly voltageFieldsLL = [0x85, 0x86, 0x87, 0x88, 0x89, 0x8A];
	readonly voltageReferenceLL = 0x80;
	readonly overCurrentsValues = [1, 2, 3, 4];
	permissionsApplied = false;

	constructor(
		private deviceService: DeviceService,
		private commonService: CommonService,
		private usersService: UsersService,
		private translateService: TranslateService,
	) { }

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		// Shifted into the device zone by getZoneTimestampFromUTC before formatting.
		timestamp_utc: TimeSource.Device,
	};

	ngOnInit() {
		this.allColumnConfig = [
			{ key: 'sequence_id', name: this.translateService.instant('glitches.sequence_id'), type: 'number' },
			{ key: 'timestamp_utc', name: this.translateService.instant('glitches.timestamp_utc'), type: 'date' },
			{ key: 'power_event_id', name: this.translateService.instant('glitches.power_event_id'), type: 'string' },
			{ key: 'power_event_value', name: this.translateService.instant('glitches.power_event_value'), type: 'number' },
			{ key: 'version', name: this.translateService.instant('g.version'), type: 'number' },
		];
		// Applied to allColumnConfig only: filterColumnsByPermissions() below narrows the
		// same column objects rather than rebuilding them, so the tooltip survives.
		this.commonService.applyTimeSourceTooltips(this.allColumnConfig, this.timeSourceByColumn);
		this.columnConfig = this.allColumnConfig;

		this.filterColumnsByPermissions();

		this.commonService.setPageTabTitle(
			`${this.currentSite.customer_name} - ${this.currentSite.name} - ${this.device.serial_number} - ${this.translateService.instant('nav.glitches')}`
		);

		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device.serial_number,
			includeSpacing: false
		});
	}

	filterColumnsByPermissions() {
		if (!this.enterprisePermissions || this.permissionsApplied)
			return;

		this.columnConfig = this.allColumnConfig.filter(col => this.usersService.hasAccessPermission(this.enterprisePermissions, `glitch.${col.key}`));
		this.permissionsApplied = true;
	}

	getGlitches() {
		let fromDateRange = this.dateRange.fromDate;
		let toDateRange = this.dateRange.toDate;

		if (!this.device.mac_address || !fromDateRange || !toDateRange)
			return;

		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let fromDate: any = new Date(new Date(fromDateRange).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(toDateRange).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();
		const zoneId = this.device.config_info.zone_id ? this.device.config_info.zone_id : this.currentSite.zone_id;
		fromDate = this.commonService.getUTCTimestampFromZone(zoneId, fromDate);
		toDate = this.commonService.getUTCTimestampFromZone(zoneId, toDate);

		this.deviceService.getGlitches(this.device.mac_address, fromDate, toDate).subscribe((response: any) => {
			if (response.length)
				this.emptyResponse = false;
			else
				this.emptyResponse = true;

			const tableData: TableData[] = [];

			response.forEach((record: any) => {
				const glitch: any = this.commonService.decompress(record, 'glitch', this.device.config_info.sct_device_type);

				glitch.timestamp_utc = this.commonService.getZoneTimestampFromUTC(zoneId, +glitch.timestamp_utc);
				glitch.timestamp_utc = moment(glitch.timestamp_utc * 1000).utc().format('MM/DD/YYYY HH:mm:ss');

				const powerEventId = +glitch.power_event_id;

				if (this.voltageFields.includes(powerEventId) && this.device.config_info.voltage_reference === this.deviceService.lineToLine) {
					glitch.power_event_value = this.commonService.calcVoltageReference(+glitch.power_event_value, 'multi');
				} else if ((this.voltageReferenceLL & powerEventId) && this.device.config_info.voltage_reference === this.deviceService.lineToNeutral) {
					glitch.power_event_value = this.commonService.calcVoltageReference(+glitch.power_event_value, 'divide');
				}

				const mappedType = this.deviceService.glitchEventTypeMapper[glitch.power_event_id];
				glitch.power_event_id = this.translateService.instant(`glitches.${mappedType}`);

				if (this.overCurrentsValues.includes(powerEventId)) {
					let energyType = 'generated';
					if (+glitch.power_event_value < 0)
						energyType = 'consumed';
					glitch.power_event_id += ` (${this.translateService.instant(`glitches.${energyType}`)})`;
				}
				glitch.power_event_value = parseFloat(Math.abs(+glitch.power_event_value).toFixed(2));

				tableData.push({
					sequence_id: { value: glitch.sequence_id },
					timestamp_utc: { value: glitch.timestamp_utc },
					power_event_id: { value: glitch.power_event_id },
					power_event_value: { value: glitch.power_event_value },
					version: { value: glitch.version },
				});
			});

			this.tableData = tableData;
		});
	}

	ngOnChanges() {
		this.filterColumnsByPermissions();
		this.getGlitches();
	}
}
